import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { useResettablePagination } from '../../hooks/useResettablePagination';
import { Box, Typography, Select, MenuItem, InputBase, LinearProgress, Tooltip, useTheme } from '@mui/material';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import TableComponent from '../TableComponent';
import { useParams } from 'react-router-dom';
import CustomChip from '../chips/CustomChip';
import { getTestLaunchesForGroup, updateTestLaunchStatus } from '../../utils/api/testlaunch_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useEffect } from 'react';
import { useGridApiRef } from '@mui/x-data-grid';
import { renderTableCell } from '../../utils/commonfunctions';
// import { fetchTestLaunchById } from '../../utils/api'; // COMMENTED OUT - No longer auto-updating launch status
import DEFAULT_ROUTES from '../../constants/routes';
import { useSelector } from 'react-redux';
import { InputBox, Selectbox } from '../FormComponent';
// Styles
import styles from '../../styles/launchesTable/index.module.scss';
const { TestGroupStatus } = require('../../enums/TestGroupEnums');
import { formatStatusKey } from '../TestLaunchDetail/statusColorPallete';
const { TestLaunchStatus } = require('../../enums/TestLaunchEnums');
import { useSnackbar } from "../../utils/useSnackbar";
import ChangeStatus from '../chips/ChangeStatus';
import { getTestLaunchStatusVariant, getStatusIcon } from '../chips/ChipComponent/chipVariants';
import TestGroupLaunchesEmptyPanel from './TestGroupLaunchesEmptyPanel';
import { getLaunchExecutionDurationDisplay } from './launchExecutionDurationDisplay';
import moment from 'moment';


const statusChipStyles = {
  Passed:   { color: '#dcfce7', textColor: '#16a34a', borderColor: '#bbf7d0', icon: <CheckCircleIcon className={`${styles['status-icon']} ${styles['passed']}`} /> },
  Failed:   { color: '#fee2e2', textColor: '#dc2626', borderColor: '#fecaca', icon: <XCircleIcon className={`${styles['status-icon']} ${styles['failed']}`} /> },
  Mixed:    { color: '#fef9c3', textColor: '#ca8a04', borderColor: '#fde68a', icon: <ClockIcon className={`${styles['status-icon']} ${styles['mixed']}`} /> },
  Pending:  { color: '#f3f4f6', textColor: '#64748b', borderColor: '#e5e7eb', icon: <ClockIcon className={`${styles['status-icon']} ${styles['pending']}`} /> },
};

const LaunchesTable = forwardRef(({ groupId, onCreateLaunch, isCreateLaunchDisabled }, ref) => {
  const {group_id} = useParams();  
  const [launches, setLaunches] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const apiRef = useGridApiRef();
  const [filters, setFilters] = useState({ search: '', status: 'all' });
  // Pagination state — snaps back to page 0 when the search/status filter changes.
  const [paginationModel, setPaginationModel] = useResettablePagination({
    pageSize: 25,
    resetKey: `${filters.search}|${filters.status}`,
  });
  const { selectedProject } = useSelector((state) => state.projects);
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  

  const onFilterChange = (newFilters) => {
    setLoading(true);
    setFilters(newFilters);
  };

  // Function to fetch launches data
  const fetchLaunchesData = async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const response = await getTestLaunchesForGroup({
        testGroupId: groupId,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: filters.search,
        status: filters.status
      });
      
      if (response.status === STATUS.SUCCESS) {
        const res = response.data || {};
        // Map API data to table rows
        setLaunches(
          (res.rows || []).map(l => ({
            ...l,
            launchDate: l.createdAt ? l.createdAt : '',
          }))
        );
        setTotal(res.total || 0);
      } else {
        setLaunches([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching launches:', error);
      setLaunches([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Expose refresh function to parent component
  useImperativeHandle(ref, () => ({
    refresh: fetchLaunchesData
  }), [groupId, paginationModel.page, paginationModel.pageSize, filters.search, filters.status]);

  useEffect(() => {
    fetchLaunchesData();
  }, [groupId, paginationModel.page, paginationModel.pageSize, filters.search, filters.status]);

  // COMMENTED OUT - No longer auto-updating launch status based on execution results
  // const handleRefreshRow = async (launchId) => {
  //   try {
  //     const updated = await fetchTestLaunchById(launchId);
  //     if (updated) {
  //       setLaunches((prev) =>
  //         prev.map((row) => (row.id === launchId ? { ...row, ...updated } : row))
  //       );
  //       if (apiRef.current) {
  //         apiRef.current.updateRows([{ ...updated }]);
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error refreshing launch row:', error);
  //     showSnackbar("Failed to refresh launch data", "error");
  //   }
  // };

  // COMMENTED OUT - No longer auto-updating launch status based on execution results
  // const handleOverallRefresh = async () => {
  //   try {
  //     setLoading(true);
  //     await fetchLaunchesData();
  //     showSnackbar("Launch data refreshed successfully!", "success");
  //   } catch (error) {
  //     console.error('Error refreshing launch data:', error);
  //     showSnackbar("Failed to refresh launch data", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Table columns definition
  const handleStatusChange = async (newStatus, testLaunchId) => {
    try {
      const response = await updateTestLaunchStatus({ 
        launchId: testLaunchId, 
        status: newStatus 
      });
      
      if (response.status === STATUS.SUCCESS) {
        fetchLaunchesData();
        showSnackbar(response.message || `Test Launch status updated to ${formatStatusKey(newStatus)} successfully!`);
      } else {
        showSnackbar(response.error || "Status update failed", "error");
      }
    } catch (error) {
      showSnackbar("Failed to update status", "error");
      console.error('Error updating status:', error);
    }
  };

  const columns = [
    {
      field: 'key',
      headerName: 'Launch ID',
      minWidth: 100,
      maxWidth: 120,
      flex: 1,
      renderCell: (params) => (
        renderTableCell(params.value, "link", {
          type: 'link',
          to: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}${DEFAULT_ROUTES.TEST_GROUPS}/${group_id}/launch-details/${params.row.id}`,
        })
      )
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: 240,
        flex: 1,
        renderCell: (params) => (
            <span className={styles['launch-name']} title={params.row.name}>{params.row.name}</span>
        ),
    },
    {
      field: 'isSequential',
      headerName: 'Run Type',
      minWidth: 100,
      maxWidth: 120,
      flex: 0.8,
      renderCell: (params) => {
        const palette = theme.palette.customColor;
        return (
          <CustomChip
            label={params.value ? 'Sequential' : 'Parallel'}
            color={params.value ? palette.blue[50] : palette.green[50]}
            textColor={params.value ? palette.blue[600] : palette.green[600]}
            borderColor={params.value ? palette.blue[200] : palette.green[200]}
            size="small"
          />
        );
      },
    },
    {
      field: 'launchDate',
      headerName: 'Date & Time',
      minWidth: 120,
      maxWidth: 150,
      flex: 1.2,
      renderCell: (params) => (
        <Box className={styles['date-time-cell']}>
          <Typography variant="body2" className={styles['date']}>{moment(params.value).format("YYYY-MM-DD")}</Typography>
          <Typography variant="caption" className={styles['time']}>{moment(params.value).local().format("hh:mm A")}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 150,
      maxWidth: 210,
      flex: 1,
      renderCell: (params) => {
        return (
          <ChangeStatus
            status={params.value}
            availableStatuses={[TestLaunchStatus.COMPLETED, TestLaunchStatus.ABORTED]}
            getVariant={getTestLaunchStatusVariant}
            getIcon={getStatusIcon}
            formatStatus={formatStatusKey}
            onStatusChange={(newStatus) => handleStatusChange(newStatus, params.row.id)}
            confirmChange={true}
            confirmTitle="Update Status"
            confirmMessage="Are you sure you want to change the Test Launch status? This action cannot be undone."
            size="small"
            showTooltip={false}
          />
        );
      },
    },
    {
      field: 'results',
      headerName: 'Results',
      minWidth: 120,
      maxWidth: 150,
      flex: 1.2,
      renderCell: (params) => (
        <Box className={styles['results-cell']}>
          <Typography variant="body2" className={styles['results-text']}>{params.row.passed}/{params.row.total} ({params.row.passRate}%)</Typography>
          <LinearProgress 
            variant="determinate" 
            value={params.row.passRate} 
            className={`${styles['progress-bar']} ${params.row.passRate === 100 ? styles['success'] : params.row.passRate >= 80 ? styles['warning'] : styles['error']}`}
          />
        </Box>
      ),
    },
    {
      field: 'duration',
      headerName: 'Execution Duration',
      minWidth: 80,
      maxWidth: 100,
      flex: 0.8,
      renderCell: (params) =>
        getLaunchExecutionDurationDisplay(params.row.isVisibleExecutionDuration, params.value),
    },
    {
      field: 'triggeredBy',
      headerName: 'Triggered By',
      minWidth: 120,
      maxWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Box className={styles['triggered-by-cell']}>
          <Typography variant="body2" className={styles['triggered-by-text']}>{params.value}</Typography>
          <Typography variant="caption" className={styles['trigger-type-text']}>{params.row.triggerType}</Typography>
        </Box>
      ),
    },
    // COMMENTED OUT - No longer auto-updating launch status based on execution results
    // {
    //   field: 'actions',
    //   headerName: 'Actions',
    //   minWidth: 80,
    //   maxWidth: 100,
    //   flex: 1,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Stack direction="row" spacing={1} className={styles['actions-cell']}>
    //       <Tooltip title="Refresh">
    //         <IconButton size="small" onClick={() => handleRefreshRow(params.row.id)} className={styles['refresh-button']}>
    //           <ArrowPathIcon className={`${'icon'} ${styles['refresh-icon']}`} />
    //         </IconButton>
    //       </Tooltip>
    //     </Stack>
    //   ),
    // },
  ];

  const hasDefaultFilters =
    (filters.search ?? '') === '' &&
    filters.status === 'all';

  const isEmptyResult = total === 0 && hasDefaultFilters;

  const isLaunchHistoryEmpty = !loading && isEmptyResult;

  const hideFilterSection = isEmptyResult && (loading || isLaunchHistoryEmpty);

  // Filter row UI
  return (
    <Box className={styles['launches-table-container']}>
      {!hideFilterSection && (
      <Box className={styles['filter-section']}>
        <InputBox
          placeholder="Search launches..."
          value={filters.search || ''}
          onChange={e => onFilterChange({ ...filters, search: e.target.value })}
          className={styles['search-input']}
          name="search"
          label="Search launch"
        />
        <Selectbox
          value={filters.status || 'all'}
          onChange={e => onFilterChange({ ...filters, status: e.target.value })}
          className={styles['status-select']}
          name="status"
          options={[
            { value: 'all', label: 'All Status' },
            { value: TestLaunchStatus.COMPLETED, label: 'Completed' },
            { value: TestLaunchStatus.NEW, label: 'New' },
            { value: TestLaunchStatus.IN_PROGRESS, label: 'In Progress' },
            { value: TestLaunchStatus.ABORTED, label: 'Aborted' }
          ]}
        />
        <Selectbox
          value={filters.trigger || 'all'}
          onChange={e => onFilterChange({ ...filters, trigger: e.target.value })}
          className={styles['trigger-select']}
          name="trigger"
          disabled={true}
          options={[
            { value: 'all', label: 'All Triggers' },
            { value: 'Manual', label: 'Manual' },
            { value: 'Scheduled', label: 'Scheduled' }
          ]}
        />
      </Box>
      )}
      <Box className={`${styles['table-container']}`}>
                  <TableComponent
                  title="Test Launch History"
                  headerActionButtons={[
                    // COMMENTED OUT - No longer auto-updating launch status based on execution results
                    // {
                    //   name: "Refresh",
                    //   tooltip: "Refresh all launches",
                    //   icon: <ArrowPathIcon className="icon" />,
                    //   action: handleOverallRefresh,
                    //   disabled: loading,
                    //   section: 'left',
                    // }
                  ]}
                  rows={launches}
                  columns={columns}
                  isCheckbox={false}
                  isRowSelection={false}
                  loading={loading}
                  isColumnResizable={!loading}
                  isPagination={true}
                  paginationMode="server"
                  rowCount={total}
                  paginationModel={paginationModel}
                  handleCustomPaginationPageChange={(event, page)=> setPaginationModel({...paginationModel, page: page})}
                  handleCustomPaginationPageSizeChange={(pageSize)=> setPaginationModel({page: 0, pageSize: pageSize})}
                  isCustomPagination={true}
                  filterMode="client"
                  sortingMode="client"
                  disableFooter={false}
                  customStyles={{
                    '& .MuiDataGrid-cell': {
                      py: 1,
                    },
                    "& .selected-row": {
                      backgroundColor: theme.palette.customColor.blue[50],
                      "&:hover": {
                        backgroundColor: theme.palette.customColor.blue[100],
                      },
                    },
                  }}
                  className={styles['launches-table']}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 100}
                  hideColumns={['name']}
                  isData={!isLaunchHistoryEmpty}
                  // isVisibleInsideTableNoData={isLaunchHistoryEmpty}
                  customNoData={
                  (
                      <TestGroupLaunchesEmptyPanel
                        onCreateLaunch={onCreateLaunch}
                        isCreateLaunchDisabled={isCreateLaunchDisabled}
                      />
                    ) 
                  }
                />
      </Box>
    </Box>
  );
});

LaunchesTable.displayName = 'LaunchesTable';

export default LaunchesTable; 